import React, { useEffect, useState, useCallback } from 'react';
import AppShell from '@/components/AppShell';
import client, { fetchFileBlob } from '@/lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { UploadDialog } from '@/components/UploadDialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, Upload, CheckCircle2, Circle, Lock, Eye, Truck, ShieldAlert,
  Loader2, Ban, RotateCcw, FileText
} from 'lucide-react';

const CAT_LABEL = {
  CDL: "Commercial Driver's License", MEDICAL_CARD: 'DOT Medical Card', INSURANCE: 'Proof of Insurance',
  SSN: 'Social Security Number', MVR: 'Motor Vehicle Record', VEHICLE_REGISTRATION: 'Vehicle Registration',
  IFTA: 'IFTA Permits', DRUG_TEST: 'Pre-employment Drug Test', CLEARINGHOUSE: 'Clearinghouse Query',
  MRI_DRUG_TEST: 'MRI Drug Test', ANNUAL: 'Annual Review', OTHER: 'Other',
};

export default function DriverProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCat, setUploadCat] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const canReview = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);

  const load = useCallback(() => {
    client.get(`/drivers/${id}`).then((r) => setD(r.data)).catch(() => toast.error('Failed to load driver'));
  }, [id]);
  useEffect(load, [load]);

  const docByCat = (cat) => (d?.documents || []).find((x) => x.category === cat);

  const review = async (docId, action) => {
    try { await client.post(`/documents/${docId}/review`, { action }); toast.success(`Document ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openPreview = async (doc) => {
    try { const url = await fetchFileBlob(doc.id); setPreview({ url, doc }); }
    catch { toast.error('Preview unavailable (seed doc has no file)'); }
  };

  const submitOnboarding = async () => {
    setSubmitting(true);
    try { await client.post(`/drivers/${id}/submit-onboarding`); toast.success('Documents submitted for review'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cannot submit'); }
    finally { setSubmitting(false); }
  };

  const changeStatus = async (status) => {
    try { await client.post(`/drivers/${id}/status`, { status }); toast.success('Status updated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reactivate = async () => {
    try { await client.post(`/drivers/${id}/reactivate`); toast.success('Driver reactivated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!d) return <AppShell title="Driver Profile"><div className="space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-24" />)}</div></AppShell>;

  const react = d.reactivation || {};
  const comp = d.compliance || {};
  const required = (d.required_docs || []);
  const submitBlocked = react.requires_mri_drug_test && !react.has_recent_mri;

  return (
    <AppShell title="Driver Profile">
      <button onClick={() => navigate('/drivers')} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="driver-back-button"><ChevronLeft className="h-4 w-4" />Back to Drivers</button>

      {/* Header */}
      <Card className="mb-5 flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-lg font-semibold text-white">{d.full_name?.split(' ').map((x)=>x[0]).slice(0,2).join('')}</div>
          <div>
            <div className="flex items-center gap-2"><h2 className="font-display text-2xl font-semibold">{d.full_name}</h2><StatusBadge status={d.status} /></div>
            <div className="text-sm text-muted-foreground">{d.email} · {d.phone}</div>
            <div className="mt-1 flex items-center gap-2"><span className="text-xs text-muted-foreground">Compliance:</span><StatusBadge status={comp.status} /></div>
          </div>
        </div>
        {canReview && (
          <div className="flex flex-wrap gap-2">
            {d.status === 'active' && <AlertConfirm trigger={<Button variant="outline" size="sm" data-testid="driver-terminate-button"><Ban className="mr-1.5 h-4 w-4" />Terminate</Button>} title="Terminate driver?" desc="Their profile and documents will be archived." onConfirm={() => changeStatus('terminated')} />}
            {(d.status === 'terminated' || d.status === 'pending_reactivation') && <Button size="sm" onClick={reactivate} disabled={submitBlocked} data-testid="driver-reactivate-button"><RotateCcw className="mr-1.5 h-4 w-4" />Reactivate</Button>}
          </div>
        )}
      </Card>

      {/* Gating banners */}
      {!comp.shipping_unlocked && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/5 p-4" data-testid="shipping-blocked-banner">
          <Lock className="mt-0.5 h-5 w-5 text-[hsl(var(--danger))]" />
          <div><div className="font-medium text-[hsl(var(--danger))]">Shipping services blocked</div><p className="text-sm text-muted-foreground">This driver cannot be dispatched to loads until all required documents are approved{comp.expired?.length ? ' and no documents are expired' : ''}.</p></div>
        </div>
      )}
      {react.requires_mri_drug_test && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4" data-testid="mri-required-banner">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-[hsl(var(--warning-foreground))]" />
          <div><div className="font-medium">MRI Drug Test required</div><p className="text-sm text-muted-foreground">This driver has been inactive for {react.days_inactive} days (30+). A new MRI Drug Test must be uploaded before submitting documents or reactivating. {react.has_recent_mri ? '\u2705 Recent MRI on file.' : '\u26a0\ufe0f Not yet uploaded.'}</p></div>
        </div>
      )}

      <Tabs defaultValue="onboarding">
        <TabsList className="mb-4">
          <TabsTrigger value="onboarding" data-testid="driver-onboarding-tab">Onboarding & Documents</TabsTrigger>
          <TabsTrigger value="info" data-testid="driver-info-tab">Driver Info</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="driver-vehicles-tab">Assigned Vehicles</TabsTrigger>
        </TabsList>

        {/* Onboarding wizard */}
        <TabsContent value="onboarding" className="space-y-3">
          <div className="grid gap-3">
            {required.map((rq, i) => {
              const doc = docByCat(rq.category);
              const done = doc && doc.status === 'approved';
              return (
                <Card key={rq.category} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`onboarding-step-${rq.category}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold">{done ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" /> : <span className="text-muted-foreground">{i + 1}</span>}</div>
                    <div>
                      <div className="flex items-center gap-2 font-medium">{rq.label}{!rq.required && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">Optional</span>}</div>
                      {doc ? <div className="text-xs text-muted-foreground">{doc.file_name} {doc.expires_at ? `\u00b7 exp ${new Date(doc.expires_at).toLocaleDateString()}` : ''}</div> : <div className="text-xs text-muted-foreground">Not uploaded</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc ? <StatusBadge status={doc.status} /> : <StatusBadge status="incomplete" label="Missing" />}
                    {doc && <Button variant="ghost" size="sm" onClick={() => openPreview(doc)} data-testid={`onboarding-view-${rq.category}`}><Eye className="h-4 w-4" /></Button>}
                    <Button variant="outline" size="sm" onClick={() => { setUploadCat(rq.category); setUploadOpen(true); }} data-testid={`onboarding-upload-${rq.category}`}><Upload className="mr-1.5 h-4 w-4" />{doc ? 'Replace' : 'Upload'}</Button>
                    {canReview && doc && doc.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => review(doc.id, 'approve')} data-testid={`onboarding-approve-${rq.category}`}>Approve</Button>
                        <Button variant="danger" size="sm" className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" onClick={() => review(doc.id, 'reject')} data-testid={`onboarding-reject-${rq.category}`}>Reject</Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* MRI drug test tile when required */}
          {react.requires_mri_drug_test && (
            <Card className="flex items-center justify-between border-[hsl(var(--warning))]/40 p-4">
              <div className="flex items-center gap-3"><ShieldAlert className="h-5 w-5 text-[hsl(var(--warning-foreground))]" /><div><div className="font-medium">MRI Drug Test (reactivation)</div><div className="text-xs text-muted-foreground">Required after 30+ days inactive</div></div></div>
              <div className="flex items-center gap-2">{react.has_recent_mri ? <StatusBadge status="approved" label="On File" /> : <StatusBadge status="expired" label="Required" />}<Button variant="outline" size="sm" onClick={() => { setUploadCat('MRI_DRUG_TEST'); setUploadOpen(true); }} data-testid="onboarding-upload-MRI"><Upload className="mr-1.5 h-4 w-4" />Upload</Button></div>
            </Card>
          )}

          <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-4">
            <div className="text-sm text-muted-foreground">
              {comp.missing?.length ? `${comp.missing.length} required document(s) missing.` : comp.pending?.length ? 'Awaiting admin approval.' : 'All required documents present.'}
            </div>
            <Button onClick={submitOnboarding} disabled={submitting || submitBlocked} data-testid="submit-documents-button">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitBlocked ? 'Blocked \u2014 MRI Required' : 'Submit Documents'}
            </Button>
          </div>
        </TabsContent>

        {/* Driver info edit */}
        <TabsContent value="info"><DriverInfoForm driver={d} onSaved={load} /></TabsContent>

        {/* Vehicles */}
        <TabsContent value="vehicles" className="space-y-3">
          {(d.vehicles || []).length === 0 ? <Card className="p-8 text-center text-sm text-muted-foreground">No vehicles assigned.</Card> :
            (d.vehicles || []).map((v) => (
              <Card key={v.vehicle_id} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3"><Truck className="h-5 w-5 text-[hsl(var(--gold))]" /><div><div className="font-medium">#{v.unit_number} · {v.make} {v.model}</div><div className="text-xs text-muted-foreground">{v.unit_type.replace('_',' ')} · VIN {v.vin?.slice(-6)}</div></div></div>
                <StatusBadge status={v.status} />
              </Card>
            ))}
        </TabsContent>
      </Tabs>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} driverId={id} defaultCategory={uploadCat} lockCategory onUploaded={load} />

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" />{preview?.doc?.file_name}</DialogTitle></DialogHeader>
          {preview && (preview.doc.content_type?.includes('image') ? <img src={preview.url} alt="doc" className="max-h-[70vh] w-full object-contain" /> : <iframe title="doc" src={preview.url} className="h-[70vh] w-full rounded-lg border" />)}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function AlertConfirm({ trigger, title, desc, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{desc}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onConfirm}>Confirm</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DriverInfoForm({ driver, onSaved }) {
  const [f, setF] = useState(driver);
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(driver), [driver]);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { first_name: f.first_name, middle_name: f.middle_name || '', last_name: f.last_name, email: f.email, phone: f.phone || '', dob: f.dob || '', ssn: f.ssn || '', address: f.address || '', city: f.city || '', state: f.state || '', zip: f.zip || '', hire_date: f.hire_date || '', license_type: f.license_type || 'CDL A', license_number: f.license_number || '', license_state: f.license_state || '', endorsements: f.endorsements || [] };
      await client.put(`/drivers/${driver.driver_id}`, payload);
      toast.success('Driver information saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const F = ({ label, k, ...p }) => (<div><Label>{label}</Label><Input value={f[k] || ''} onChange={(e) => upd(k, e.target.value)} className="mt-1" data-testid={`driver-field-${k}`} {...p} /></div>);
  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Update Driver Information</h3><Button onClick={save} disabled={saving} data-testid="driver-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button></div>
      <div className="grid gap-4 md:grid-cols-3">
        <F label="First Name *" k="first_name" /><F label="Middle Name" k="middle_name" /><F label="Last Name *" k="last_name" />
        <F label="Phone" k="phone" /><F label="Email *" k="email" /><F label="DOB" k="dob" />
        <F label="Address" k="address" /><F label="City" k="city" /><F label="State" k="state" />
        <F label="Zip" k="zip" /><F label="Hire Date" k="hire_date" /><F label="License Type" k="license_type" />
        <F label="License #" k="license_number" /><F label="License State" k="license_state" /><F label="SSN" k="ssn" />
      </div>
    </Card>
  );
}
